<template>
  <div>
    <h3 class="text-2xl font-semibold mb-4">Заявки</h3>

    <!-- Поле поиска и фильтры -->
    <div class="flex flex-wrap items-center gap-4 mb-6">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Поиск по имени..."
        class="w-full sm:w-1/3 px-4 py-2 border rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div class="flex gap-2">
        <button
          @click="setFilter('all')"
          :class="filter === 'all' ? activeClass : defaultClass"
        >
          Все
        </button>
        <button
          @click="setFilter('doctor')"
          :class="filter === 'doctor' ? activeClass : defaultClass"
        >
          Врачи
        </button>
        <button
          @click="setFilter('nurse')"
          :class="filter === 'nurse' ? activeClass : defaultClass"
        >
          Медсестры
        </button>
      </div>
    </div>

    <!-- Список заявок -->
    <div
      v-for="(application, index) in filteredApplications"
      :key="application.id"
      :class="[
        'application-card bg-white shadow-md rounded-lg p-4 mb-6 relative',
        application.is_updated ? 'border-2 border-yellow-500 animate-pulse' : ''
      ]"
    >
      <div
        @click="toggleDropdown(index)"
        class="application-header flex justify-between items-center cursor-pointer"
      >
        <div>
          <!-- Номер заявки, имя, ИИН и дата создания -->
          <p class="text-lg font-bold">
            {{ index + 1 }}. {{ application.full_name }}
          </p>
          <p class="text-sm text-gray-500"><strong>ИИН:</strong> {{ application.iin || "N/A" }}</p>
          <p class="text-sm text-gray-500">
            <strong>Дата создания:</strong> {{ formatDate(application.created_at) }}
          </p>
          <p class="text-sm text-gray-500">{{ application.gender }}</p>
          <p class="text-sm text-gray-500">{{ application.date_of_birth }}</p>
        </div>
        <div class="flex items-center">
          <span
            v-if="application.is_updated"
            class="text-yellow-500 font-bold mr-2"
          >
            Обновлено
          </span>
          <span class="text-blue-500 font-semibold">Подробнее</span>
        </div>
      </div>

      <transition name="fade">
        <div v-if="openedDropdown === index" class="application-details mt-4">
          <p><strong>Категория:</strong> {{ application.category || "N/A" }}</p>
          <p><strong>Телефон:</strong> {{ application.phone_number || "N/A" }}</p>
          <p><strong>Email:</strong> {{ application.email || "N/A" }}</p>
          <p><strong>Описание:</strong> {{ application.description || "N/A" }}</p>
          <p><strong>Город:</strong> {{ application.city || "N/A" }}</p>

          <div v-if="application.certificates" class="certificate-buttons mt-4">
            <p><strong>Сертификаты:</strong></p>
            <div class="flex flex-wrap gap-2 mt-2">
              <a
                v-for="(cert, index) in application.certificates"
                :key="cert"
                :href="cert"
                target="_blank"
                class="certificate-button px-4 py-2 bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-600"
              >
                Сертификат {{ index + 1 }}
              </a>
            </div>
          </div>

          <div
            v-if="application.experience && application.experience.length"
            class="mt-4"
          >
            <p><strong>Опыт работы:</strong></p>
            <ul class="list-disc ml-5 mt-2">
              <li v-for="(exp, idx) in application.experience" :key="idx">
                {{ exp.place }} — {{ exp.years }} лет
              </li>
            </ul>
          </div>

          <div class="mt-4 flex justify-end gap-4">
            <button
              @click="confirmApplication(application.id, application.type)"
              class="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded shadow-md transition duration-200"
            >
              Подтвердить
            </button>
            <button
              @click="rejectApplication(application.id, application.rejectionReason, application.type)"
              class="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded shadow-md transition duration-200"
            >
              Отказать
            </button>
            <button
              @click="sendDocument(application.id, application.type)"
              class="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded shadow-md transition duration-200"
            >
              Отправить документ
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import {
  getVerify,
  confirmApplicationById,
  rejectApplicationById,
  resetUpdateIndicator,
  sendDocument,
} from "../services/verificationService";

export default {
  data() {
    return {
      applications: [],
      searchQuery: "",
      filter: "all",
      openedDropdown: null,
      activeClass: "bg-blue-500 text-white font-semibold px-4 py-2 rounded-full shadow",
      defaultClass: "bg-gray-200 text-gray-700 px-4 py-2 rounded-full hover:bg-gray-300 transition",
    };
  },
  computed: {
    filteredApplications() {
      return this.applications
        .filter((app) => this.filter === "all" || app.type === this.filter)
        .filter((app) =>
          app.full_name.toLowerCase().includes(this.searchQuery.toLowerCase())
        )
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("ru-RU", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    async loadApplications() {
      try {
        const data = await getVerify();
        this.applications = data.map((app) => ({
          ...app,
          type: app.type || "doctor",
          email: app.email || "N/A",
          is_updated: app.is_updated || false,
          iin: app.iin || "N/A",
          created_at: app.created_at || null,
        }));
      } catch (error) {
        console.error("Ошибка при загрузке заявок:", error);
      }
    },
    setFilter(type) {
      this.filter = type;
    },
    toggleDropdown(index) {
      const application = this.filteredApplications[index];
      if (application && application.is_updated) {
        this.markAsViewed(application.id);
      }
      this.openedDropdown = this.openedDropdown === index ? null : index;
    },
    async confirmApplication(applicationId, type) {
      try {
        await confirmApplicationById(applicationId, type);
        this.applications = this.applications.filter(
          (app) => app.id !== applicationId
        );
      } catch (error) {
        console.error("Ошибка при подтверждении заявки:", error);
      }
    },
    async rejectApplication(applicationId, rejectionReason, type) {
      try {
        await rejectApplicationById(applicationId, rejectionReason, type);
        this.applications = this.applications.filter(
          (app) => app.id !== applicationId
        );
      } catch (error) {
        console.error("Ошибка при отказе заявки:", error);
      }
    },
    async sendDocument(applicationId, type) {
      try {
        const response = await sendDocument(applicationId, type);
        console.log("Документ отправлен:", response);
      } catch (error) {
        console.error("Ошибка при отправке документа:", error);
      }
    },
    async markAsViewed(applicationId) {
      try {
        await resetUpdateIndicator(applicationId);
        const application = this.applications.find(
          (app) => app.id === applicationId
        );
        if (application) {
          application.is_updated = false;
        }
      } catch (error) {
        console.error("Ошибка при сбросе индикации:", error);
      }
    },
  },
  mounted() {
    this.loadApplications();
  },
};
</script>

<style scoped>
.application-card {
  position: relative;
}
.application-header .text-yellow-500 {
  font-size: 14px;
  font-weight: bold;
}
</style>
